import { z } from 'zod';
import { contactBookOrgQuery } from './contact-book';
import { personIdSchema, userPersonIdSchema } from './person-id';

export { CONTACT_PERSON_ID_PATTERN, PERSON_ID_FORM_MESSAGE, PERSON_ID_PATTERN, personIdSchema, userPersonIdSchema } from './person-id';

/**
 * Person links and the deny list (wayai-pro/wayai#5473 §1, D5, D6), served on `/api/contact-book`
 * (`routes/api/contact-book/person-links.ts`) and held by the org's `ContactBookDO`, which loads this
 * module for its caps. Apart from `contact-book.ts`, so UserDO and the other callers of the contact
 * book's contract that serve no person link load none of it.
 *
 * A person link says a user is
 * also another person id: a contact of the book, or another user's person id (a returning person's
 * old identity); writing one also copies the target's own links, at most `PERSON_LINKS_MAX` per
 * user. The deny list holds the person ids the org denied. Both are named by person ids only: the
 * user a link route acts on is the `prs_` id the path names, resolved on the org's hubs (the
 * reverse lookup), and every writer is answered as their person id, to an org admin only (a link's
 * writer holding `org:write` alone is told no person id the path did not name) — no route here answers an
 * account's id. Only a person signed in interactively writes either — never a token, however wide
 * its grants: a link takes `org:write` over the whole org (D6); the deny list, its reads, a user's
 * links and the reverse lookup take `org:admin` over the whole org. Every route that resolves a
 * person id spends the org's lookup budget (`PERSON_LOOKUP_PER_ORG_LIMIT` a minute). Every toolset
 * call reads both for each person-bound party it is for (`readPersonLinksOf`, `apps/backend/src/rekor/access-set.ts`):
 * a link widens that party's person set by its id, a denial removes one. An eval run's declared persons (its
 * scenario's `person:` ids, on a preview hub) are used as declared: neither is read for them.
 */

/**
 * The most person links one user holds (D5, as the owner set it on wayai-pro/wayai#5473: 15), so the user's own
 * person id and every link fit the 16 ids one party's person set holds (`REKOR_MAX_PERSON_IDS`).
 */
export const PERSON_LINKS_MAX = 15;

/** The most deny-list entries one page carries. */
export const PERSON_DENY_PAGE_MAX = 100;

/** The most hubs a person id's lookup reads: an org holding more is refused (`person_lookup_hubs_full`). */
export const PERSON_LOOKUP_HUBS_MAX = 200;

/** The user a person link route acts on, named by their person id. */
export const personParam = z.object({
  personId: userPersonIdSchema,
});

/** One of that user's links: the person id or contact id it points at. */
export const personLinkParam = personParam.extend({
  targetId: personIdSchema,
});

/** One deny-list entry. */
export const deniedPersonParam = z.object({
  personId: personIdSchema,
});

/** A page of the deny list, by id: `cursor` is the previous page's `next_cursor`. */
export const listDeniedPersonsQuery = contactBookOrgQuery.extend({
  cursor: personIdSchema.optional(),
  limit: z.coerce.number().int().min(1).max(PERSON_DENY_PAGE_MAX).default(50),
});

/**
 * Why a person-link, deny-list or person-lookup request was refused, as `details.reason` carries it;
 * a link the account's book index refuses carries `link_orgs_full` (`CONTACT_BOOK_REFUSALS`).
 */
export const PERSON_LINK_REFUSALS = [
  /** A person link to the user's own person id. */
  'person_link_self',
  /** A person link to a contact id the book does not hold. */
  'person_link_unknown_contact',
  /** The link would give the user more than `PERSON_LINKS_MAX` links: refused whole. */
  'person_links_full',
  /** The org has more hubs than a person id's lookup reads (`PERSON_LOOKUP_HUBS_MAX`). */
  'person_lookup_hubs_full',
] as const;
export type PersonLinkRefusal = typeof PERSON_LINK_REFUSALS[number];

/** A person link, or a deny-list entry: what it names, its writer's person id, and when. */
export interface PersonLinkEntry {
  /** A user's person id, or a contact id of the book. */
  person_id: string;
  /** The writer's person id in this org. */
  created_by: string;
  created_at: string;
}

/**
 * A user's person links, oldest first: at most `PERSON_LINKS_MAX`, or one more for a user who holds more than
 * the cap (the cap was 16 before it was 15), listed whole so an admin can remove the extra one.
 */
export interface ListPersonLinksResponse {
  /** The user's own person id. */
  person_id: string;
  links: PersonLinkEntry[];
}

/**
 * A link write: `linked` wrote ids new to the user (the target and the target's own links), `unchanged`
 * none. `added` (those ids) and `links` (the user's links after it) answer only an org admin, as a
 * user's links do: a writer holding `org:write` alone is told the status, and no person id but the two
 * its path named.
 */
export interface LinkPersonResponse {
  /** The user's own person id. */
  person_id: string;
  status: 'linked' | 'unchanged';
  added?: string[];
  links?: PersonLinkEntry[];
}

export interface DenyPersonResponse {
  person_id: string;
  /** `unchanged`: the id was denied already, and its first entry stands. */
  status: 'denied' | 'unchanged';
}

export interface ListDeniedPersonsResponse {
  denied: PersonLinkEntry[];
  /** Where the next page starts; null on the last page. */
  next_cursor: string | null;
}

/**
 * Whom a person id names in the org (§1 "Reverse lookup"), as one of the org's hubs knows them: the
 * hub where a conversation carries it, and that hub's name and email for the person. `unknown` when
 * no conversation of the org's hubs carries it. Never the account's id.
 */
export type PersonLookup =
  | { person_id: string; status: 'found'; hub_id: string; hub_name: string | null; user_name: string | null; user_email: string | null }
  | { person_id: string; status: 'unknown' };

export interface PersonLookupResponse {
  person: PersonLookup;
}
